import {
  Connection,
  useConnection,
  useConnections,
  useIntegrations,
} from '@membranehq/react';
import type { Integration, IntegrationAppClient } from '@membranehq/sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getOAuthOptionKey, OAuthStatus } from 'src/utils/integrationUtils';

interface ConnectionBeforeOAuth {
  id: string | null;
  disconnected: boolean | undefined;
  updatedAt: string | null;
  initialConnectionsCount: number;
  initialConnectionsCountForIntegration: number;
}

interface UseOAuthConnectionProps {
  integrationKey: string | undefined;
  integration: Integration | null;
  integrationApp: IntegrationAppClient;
}

const POLLING_INTERVAL = 2500;

export const useOAuthConnection = ({
  integrationKey,
  integration,
  integrationApp,
}: UseOAuthConnectionProps) => {
  const [oauthStatus, setOauthStatus] = useState<OAuthStatus>(OAuthStatus.Idle);
  const [lastSuccessMessage, setLastSuccessMessage] = useState<string | null>(
    null,
  );
  const connectionBeforeOAuthRef = useRef<ConnectionBeforeOAuth | null>(null);
  const isRefreshingRef = useRef(false);

  const setOauthStatusWithReset = useCallback((status: OAuthStatus) => {
    setOauthStatus(status);
    if (status === OAuthStatus.Idle) {
      setLastSuccessMessage(null);
    }
  }, []);

  const { refresh: refreshIntegrations } = useIntegrations();
  const {
    connections,
    refresh: refreshConnections,
    loading: connectionsLoading,
  } = useConnections();
  const { connection: existingConnection, refresh: refreshExistingConnection } =
    useConnection(integrationKey);

  const completeConnection = useCallback(
    (
      beforeOAuth: ConnectionBeforeOAuth,
      currentConnection?: Connection | null,
    ) => {
      const hadConnectionBefore = beforeOAuth.id !== null;
      const message = hadConnectionBefore
        ? currentConnection?.id !== beforeOAuth.id
          ? 'Connection updated successfully'
          : 'Connection reconnected successfully'
        : 'Connection created successfully';

      setLastSuccessMessage(message);
      setOauthStatus(OAuthStatus.Success);
      connectionBeforeOAuthRef.current = null;
    },
    [],
  );

  const triggerRefreshes = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try {
      await refreshExistingConnection();
      await refreshIntegrations();
      await refreshConnections();
    } catch (error) {
      console.error('Error refreshing connection state:', error);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [refreshExistingConnection, refreshIntegrations, refreshConnections]);

  // When connection state changes (e.g. after refreshes), evaluate whether OAuth completed
  useEffect(() => {
    if (oauthStatus !== OAuthStatus.Waiting || !integrationKey) return;

    const beforeOAuth = connectionBeforeOAuthRef.current;
    if (!beforeOAuth) return;

    const currentConnection = existingConnection;
    const hadConnectionBefore = beforeOAuth.id !== null;

    const connectionsForIntegration = connections.filter(
      (c) => (c as { integrationId?: string }).integrationId === integrationKey,
    );

    // New connection was added for this integration (run first so we can complete even when useConnection hasn't updated yet)
    if (
      connectionsForIntegration.length >
      beforeOAuth.initialConnectionsCountForIntegration
    ) {
      completeConnection(beforeOAuth, currentConnection);
      return;
    }

    if (!currentConnection) {
      return;
    }

    // First time connecting (no connection before)
    if (!hadConnectionBefore) {
      completeConnection(beforeOAuth, currentConnection);
      return;
    }

    // Reconnecting - connection ID changed (new connection replaced old one)
    if (beforeOAuth.id !== currentConnection.id) {
      completeConnection(beforeOAuth, currentConnection);
      return;
    }

    // Reconnecting - updatedAt changed
    if (
      beforeOAuth.updatedAt &&
      currentConnection.updatedAt &&
      new Date(currentConnection.updatedAt) > new Date(beforeOAuth.updatedAt)
    ) {
      completeConnection(beforeOAuth, currentConnection);
      return;
    }

    // Reconnecting - was disconnected, now connected
    if (beforeOAuth.disconnected === true && !currentConnection.disconnected) {
      completeConnection(beforeOAuth, currentConnection);
      return;
    }
  }, [
    oauthStatus,
    integrationKey,
    existingConnection,
    connections,
    completeConnection,
  ]);

  // Store connection state when OAuth starts and cleanup
  useEffect(() => {
    if (!integrationKey) return;
    if (
      oauthStatus === OAuthStatus.Idle ||
      oauthStatus === OAuthStatus.Success
    ) {
      connectionBeforeOAuthRef.current = null;
    }
    if (oauthStatus !== OAuthStatus.Waiting) {
      return;
    }

    // Only capture snapshot once when OAuth starts, after connections have loaded
    // so we don't falsely detect "new connection" when the list finishes loading
    if (connectionBeforeOAuthRef.current === null && !connectionsLoading) {
      const countForIntegration = connections.filter(
        (c) =>
          (c as { integrationId?: string }).integrationId === integrationKey,
      ).length;
      connectionBeforeOAuthRef.current = {
        id: existingConnection?.id || null,
        disconnected: existingConnection?.disconnected === true,
        updatedAt: existingConnection?.updatedAt || null,
        initialConnectionsCount: connections.length,
        initialConnectionsCountForIntegration: countForIntegration,
      };
    }
  }, [
    oauthStatus,
    integrationKey,
    existingConnection,
    connections,
    connectionsLoading,
  ]);

  // Polling and event listeners trigger refreshes; completion is handled by the effect above when state updates
  useEffect(() => {
    if (oauthStatus !== OAuthStatus.Waiting || !integrationKey) return;

    const pollingInterval = setInterval(
      () => void triggerRefreshes(),
      POLLING_INTERVAL,
    );

    const handleWindowEvent = (event: MessageEvent | FocusEvent) => {
      if (event instanceof MessageEvent) {
        const isAllowedIntegrationOrigin = (url: URL) =>
          url.protocol === 'https:' &&
          (url.hostname === 'integration.app' ||
            url.hostname.endsWith('.integration.app') ||
            url.hostname === 'getmembrane.com' ||
            url.hostname.endsWith('.getmembrane.com'));

        const isAllowedOrigin = (() => {
          try {
            if (event.origin === window.location.origin) return true;
            const url = new URL(event.origin);
            return isAllowedIntegrationOrigin(url);
          } catch {
            return false;
          }
        })();

        if (!isAllowedOrigin) return;

        const isConnectionEvent =
          event.data?.type === 'connection-created' ||
          event.data?.type === 'connection-updated' ||
          event.data === 'connection-successful';

        if (isConnectionEvent) {
          void triggerRefreshes();
        }
      } else if (event.type === 'focus') {
        void triggerRefreshes();
      }
    };

    window.addEventListener('message', handleWindowEvent);
    window.addEventListener('focus', handleWindowEvent);

    return () => {
      clearInterval(pollingInterval);
      window.removeEventListener('message', handleWindowEvent);
      window.removeEventListener('focus', handleWindowEvent);
    };
  }, [oauthStatus, integrationKey, triggerRefreshes]);

  const handleOAuthConnect = useCallback(
    async (options?: {
      input?: Record<string, unknown>;
      parameters?: Record<string, unknown>;
      authOptionKey?: string;
    }): Promise<void> => {
      if (!integrationApp || !integrationKey) return;

      connectionBeforeOAuthRef.current = null;
      setOauthStatusWithReset(OAuthStatus.Waiting);

      try {
        const authOptionKey =
          options?.authOptionKey ?? getOAuthOptionKey(integration);

        await integrationApp.integration(integrationKey).connect({
          input: options?.input ?? {},
          parameters: options?.parameters ?? {},
          ...(authOptionKey && { authOptionKey }),
        });
      } catch (error: unknown) {
        console.error('Error connecting OAuth:', error);
        setOauthStatusWithReset(OAuthStatus.Closed);
      }
    },
    [integrationApp, integrationKey, integration],
  );

  const handleRefresh = useCallback(async () => {
    await triggerRefreshes();
  }, [triggerRefreshes]);

  return {
    oauthStatus,
    setOauthStatus: setOauthStatusWithReset,
    handleOAuthConnect,
    handleRefresh,
    lastSuccessMessage,
  };
};
